import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SubjectIcon from '@mui/icons-material/Subject';
import { Box, Divider, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { Fragment, memo, useContext, useMemo } from "react";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/linkedin.svg";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    card: {
        width: 450,
        maxWidth: 450,
        minHeight: 200,
        maxHeight: 400,
        height: 'auto',
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    container: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    avatar: {
        width: 48,
        height: 48,
        minWidth: 48,
        borderRadius: '50%',
        background: '#d1fae5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    initials: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: '#065f46',
    },
    name: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 700,
        color: theme.colors.neutral['700'],
    },
    fieldIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
        color: theme.colors.neutral['400'],
    },
    linkedInIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
        fill: theme.colors.neutral['400'],
    },
    fieldLabel: {
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
        textTransform: 'uppercase' as any,
        width: 110,
        minWidth: 110,
        letterSpacing: '0.03em',
    },
    fieldValue: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.colors.neutral['700'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as any,
    },
    fieldValueLink: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.palette.primary.main,
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as any,
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    fieldValueNone: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.colors.neutral['300'],
        fontStyle: 'normal',
    },
    background: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: '#666666',
        whiteSpace: 'pre-line' as any,
    },
}));

const getInitials = (name: string) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const PersonCard: React.FC<{ element: any }> = ({ element }) => {
    const classes = useStyles();

    const name = element.title?.split('|')?.[0]?.split('-')?.[0]?.trim();
    const linkedinUrl = element.link?.includes('linkedin') ? element.link : undefined;
    const linkedInDisplay = useMemo(() => {
        if (!linkedinUrl) return null;
        try {
            const url = new URL(linkedinUrl);
            return url.hostname + url.pathname.replace(/\/$/, '');
        } catch {
            return linkedinUrl;
        }
    }, [linkedinUrl]);

    return (
        <Stack p={2} width="100%">
            {/* Avatar + Name */}
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Box className={classes.avatar}>
                    <Typography className={classes.initials}>{getInitials(name)}</Typography>
                </Box>
                <Typography className={classes.name}>{name || '[Person]'}</Typography>
            </Stack>

            {/* Field rows — indented under name */}
            <Stack spacing={1.25} pl="20px">
                {/* Title */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <BadgeOutlinedIcon className={classes.fieldIcon} />
                    <Typography className={classes.fieldLabel}>{'Title'}</Typography>
                    {element.role ? (
                        <Typography className={classes.fieldValue}>{element.role}</Typography>
                    ) : (
                        <Typography className={classes.fieldValueNone}>{'None'}</Typography>
                    )}
                </Stack>

                {/* Email */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <EmailOutlinedIcon className={classes.fieldIcon} />
                    <Typography className={classes.fieldLabel}>{'Email'}</Typography>
                    {element.email ? (
                        <Typography className={classes.fieldValueLink}
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`mailto:${element.email}`, '_blank');
                            }}>
                            {element.email}
                        </Typography>
                    ) : (
                        <Typography className={classes.fieldValueNone}>{'None'}</Typography>
                    )}
                </Stack>

                {/* LinkedIn */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <LinkedInIcon className={classes.linkedInIcon} />
                    <Typography className={classes.fieldLabel}>{'LinkedIn'}</Typography>
                    {linkedinUrl ? (
                        <Typography className={classes.fieldValueLink}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                window.open(linkedinUrl, '_blank', 'noopener, noreferrer');
                            }}>
                            {linkedInDisplay}
                        </Typography>
                    ) : (
                        <Typography className={classes.fieldValueNone}>{'None'}</Typography>
                    )}
                </Stack>

                {/* Location */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <LocationOnOutlinedIcon className={classes.fieldIcon} />
                    <Typography className={classes.fieldLabel}>{'Location'}</Typography>
                    {element.location ? (
                        <Typography className={classes.fieldValue}>{element.location}</Typography>
                    ) : (
                        <Typography className={classes.fieldValueNone}>{'None'}</Typography>
                    )}
                </Stack>

                {/* Background */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <SubjectIcon className={classes.fieldIcon} />
                    <Typography className={classes.fieldLabel}>{'Background'}</Typography>
                    {element.content ? (
                        <Typography className={classes.background}>
                            {element.content?.replace('- ', '• ')?.replaceAll('\n-', '\n •')}
                        </Typography>
                    ) : (
                        <Typography className={classes.fieldValueNone}>{'None'}</Typography>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

const KeyPeopleContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);

    const keyPeople = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:Key People`)
    , [dashboard.id, mappedOverviewQueries]);

    if (!Array.isArray(keyPeople) || keyPeople.length === 0)
        return (<></>);

    return (<>
        <Stack className={classes.card}>
            <Stack className={classes.container} alignItems="flex-start" justifyContent="stretch">
                {keyPeople?.map((element: any, i) => (
                    <Fragment key={'key-people-content-card-196-' + i}>
                        {(i > 0) && <Divider sx={{ width: '100%' }} />}
                        <PersonCard element={element} />
                    </Fragment>
                ))}
            </Stack>
        </Stack>
    </>);
};

export default memo(KeyPeopleContentCard);
